// Smart form helpers: address autocomplete, VIN decode, plate match, USDOT/MC carrier lookup
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

type Values = Record<string, unknown>;
type Patch = (p: Values) => void;
const empty = (v: unknown) => v === undefined || v === null || String(v).trim() === '';

/** What the server has set up (address autocomplete needs a Google key, MC lookups an FMCSA key) */
export const useLookupStatus = () => useQuery({
  queryKey: ['lookup-status'], staleTime: Infinity, retry: false,
  queryFn: () => api.get<{ address: boolean; carrier_mc: boolean; carrier_dot: boolean }>('/lookup/status')
});

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

const newSession = () => (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, '');

// ---------- Address ----------
type Hit = { id: string; main: string; secondary: string };

/** Text input that suggests full addresses as you type ("1200 Indus…") and fills the picked one */
export function AddressInput({ value, onChange, required, placeholder, country = 'US', disabled }: {
  value: string; onChange: (v: string) => void; required?: boolean; placeholder?: string; country?: string; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const status = useLookupStatus();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [typed, setTyped] = useState(false);          // only search what the person typed, not values loaded into the form
  const session = useRef(newSession());
  const q = useDebounced(value.trim(), 250);
  const enabled = !!status.data?.address && typed && q.length >= 3;
  const hits = useQuery({
    queryKey: ['addr', q, country], enabled, staleTime: 60_000, retry: false,
    queryFn: () => api.get<{ items: Hit[] }>(`/lookup/address?q=${encodeURIComponent(q)}&session=${session.current}&country=${country}`).then(r => r.items)
  });
  const items = enabled ? hits.data ?? [] : [];
  const show = open && items.length > 0;

  const pick = async (h: Hit) => {
    setOpen(false); setTyped(false);
    onChange(`${h.main}, ${h.secondary}`);
    try {
      const r = await api.get<{ address: string }>(`/lookup/address/${encodeURIComponent(h.id)}?session=${session.current}`);
      if (r.address) onChange(r.address);
    } catch { /* keep the suggestion text */ }
    session.current = newSession();                    // one billing session per picked address
  };

  return (
    <div className="combo">
      <input value={value} required={required} disabled={disabled} autoComplete="street-address" placeholder={placeholder ?? (status.data?.address ? t('lookup.address_hint') : undefined)}
        role="combobox" aria-expanded={show} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={show && active >= 0 ? `${listId}-${active}` : undefined}
        onChange={e => { setTyped(true); setOpen(true); setActive(-1); onChange(e.target.value); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={e => {
          if (!show) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
          else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); void pick(items[active]!); }
          else if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
        }} />
      {show && (
        <ul className="combo-list" id={listId} role="listbox">
          {items.map((h, i) => (
            <li key={h.id} id={`${listId}-${i}`} role="option" aria-selected={i === active} className={i === active ? 'on' : ''}
              onMouseDown={e => { e.preventDefault(); void pick(h); }}>
              <b>{h.main}</b><span>{h.secondary}</span>
            </li>
          ))}
          <li className="combo-foot" aria-hidden="true">Google</li>
        </ul>
      )}
    </div>
  );
}

// ---------- Result card ----------
function Hint({ tone = 'info', children }: { tone?: 'info' | 'ok' | 'warn'; children: React.ReactNode }) {
  return <div className={`hint full hint-${tone}`} role="status" aria-live="polite">{children}</div>;
}

const VIN_OK = /^[A-HJ-NPR-Z0-9]{17}$/;
type UnitHit = { id: number; unit_number: string; kind: string; year: number | null; make: string | null; model: string | null; company_name: string; plate?: string | null; vin?: string | null };
const unitLine = (u: UnitHit) => [u.unit_number, [u.year, u.make, u.model].filter(Boolean).join(' '), u.company_name].filter(Boolean).join(' · ');


// ---------- "Fill these fields?" ----------
/** Suggested values wait for the person to confirm. After filling, every field stays editable and is never refilled;
 *  "Undo" puts back what was there before. */
function useApply(key: string, suggest: Values, values: Values, patch: Patch) {
  const [state, setState] = useState<{ key: string; mode: 'ask' | 'applied' | 'dismissed'; before?: Values }>({ key, mode: 'ask' });
  const cur = state.key === key ? state : { key, mode: 'ask' as const };
  const fields = Object.entries(suggest).filter(([k, v]) => k in values && v !== undefined && v !== null && v !== '' && String(values[k] ?? '') !== String(v));
  const apply = () => {
    const before = Object.fromEntries(fields.map(([k]) => [k, values[k]]));
    patch(Object.fromEntries(fields.map(([k, v]) => [k, String(v)])));
    setState({ key, mode: 'applied', before });
  };
  const undo = () => { if (cur.before) patch(cur.before); setState({ key, mode: 'dismissed' }); };
  const dismiss = () => setState({ key, mode: 'dismissed' });
  return { mode: cur.mode, fields, apply, undo, dismiss };
}

const FIELD_LABEL: Record<string, string> = {
  make: 'unit.make', model: 'unit.model', year: 'unit.year', kind: 'unit.kind', axles: 'unit.axles', trailer_type: 'unit.trailer_type', vin: 'unit.vin',
  company_name: 'cust.company', address: 'cust.address', phone: 'cust.phone', usdot: 'cust.usdot'
};

function ApplyBar({ a }: { a: ReturnType<typeof useApply> }) {
  const { t } = useTranslation();
  if (a.mode === 'applied') return (
    <div className="hint-actions"><span className="hint-done">✓ {t('lookup.applied')}</span>
      <button type="button" className="btn sm" onClick={a.undo}>{t('lookup.undo')}</button></div>
  );
  if (a.mode === 'dismissed' || !a.fields.length) return null;
  const show = (k: string, v: unknown) => k === 'kind' ? t('kind.' + v) : k === 'trailer_type' ? t('ttype.' + v) : String(v);
  return (
    <div className="hint-actions">
      <span className="hint-will">{t('lookup.will_fill')}: {a.fields.map(([k, v]) => `${t(FIELD_LABEL[k] ?? k)} — ${show(k, v)}`).join(' · ')}</span>
      <span className="hint-btns">
        <button type="button" className="btn sm primary" onClick={a.apply}>{t('lookup.fill_yes')}</button>
        <button type="button" className="btn sm" onClick={a.dismiss}>{t('lookup.fill_no')}</button>
      </span>
    </div>
  );
}

// ---------- VIN ----------
type VinResult = { vehicle: Record<string, string>; suggest: Values; existing: UnitHit[] };

/** Decodes a full VIN, shows what it says (and any unit already on file) and fills the unit fields once confirmed */
export function VinHint({ vin, values, patch, selfId }: { vin: unknown; values: Values; patch: Patch; selfId?: number }) {
  const { t } = useTranslation();
  const v = String(vin ?? '').trim().toUpperCase();
  const ok = VIN_OK.test(v);
  const r = useQuery({ queryKey: ['lookup-vin', v], enabled: ok, staleTime: Infinity, retry: false, queryFn: () => api.get<VinResult>(`/lookup/vin/${v}`) });
  const suggest = useMemo(() => {
    const out: Values = { ...(r.data?.suggest ?? {}) };
    if (selfId) delete out.kind;                               // editing: the unit's kind stays as it is
    return out;
  }, [r.data, selfId]);
  const a = useApply(v, suggest, values, patch);

  if (!ok) return String(vin ?? '').length >= 11 ? <Hint>{t('lookup.vin_length', { n: v.length })}</Hint> : null;
  if (r.isLoading) return <Hint>{t('lookup.searching')}</Hint>;
  if (!r.data) return null;
  const d = r.data.vehicle;
  const others = r.data.existing.filter(u => u.id !== selfId);
  const title = [d.year, d.make && d.make.charAt(0) + d.make.slice(1).toLowerCase(), d.model, d.series].filter(Boolean).join(' ');
  const specs = [
    d.engine_manufacturer && [d.engine_manufacturer, d.engine_model].filter(Boolean).join(' '),
    d.displacement_l && `${Number(d.displacement_l).toFixed(1)} L`, d.engine_hp && `${Math.round(Number(d.engine_hp))} hp`,
    d.fuel, d.transmission, d.drive_type, d.brakes && `${d.brakes} ${t('lookup.brakes')}`,
    d.gvwr?.replace(/^Class (\w+):.*$/, 'GVWR Class $1'), d.body_class, d.trailer_body, d.trailer_length_ft && `${d.trailer_length_ft} ft`
  ].filter(Boolean) as string[];
  if (!title && !specs.length) return <Hint tone="warn">{t('lookup.vin_unknown')}</Hint>;
  return (
    <Hint tone={others.length ? 'warn' : 'ok'}>
      <div className="hint-title">{title || t('lookup.vin_decoded')}</div>
      {specs.length > 0 && <div className="hint-specs">{specs.join(' · ')}</div>}
      {others.map(u => <div key={u.id} className="hint-note">{t('lookup.vin_exists')} <Link to={`/units/${u.id}`}>{unitLine(u)}</Link></div>)}
      <ApplyBar a={a} />
    </Hint>
  );
}

// ---------- Plate ----------
/** Shows the units on file with this plate (same truck coming back, or a typo) */
export function PlateHint({ plate, values, patch, selfId }: { plate: unknown; values: Values; patch: Patch; selfId?: number }) {
  const { t } = useTranslation();
  const p = useDebounced(String(plate ?? '').trim().toUpperCase(), 350);
  const r = useQuery({
    queryKey: ['lookup-plate', p], enabled: p.replace(/[\s-]/g, '').length >= 3, staleTime: 30_000, retry: false,
    queryFn: () => api.get<{ units: UnitHit[] }>(`/lookup/plate?plate=${encodeURIComponent(p)}`).then(x => x.units)
  });
  const units = (r.data ?? []).filter(u => u.id !== selfId);
  const one = units.length === 1 ? units[0]! : null;
  const suggest = useMemo<Values>(() => one ? { vin: one.vin, make: one.make, model: one.model, year: one.year, kind: one.kind } : {}, [one]);
  const a = useApply(p + ':' + (one?.id ?? ''), selfId ? {} : suggest, values, patch);
  if (!units.length) return null;
  return (
    <Hint tone="warn">
      <div className="hint-title">{t('lookup.plate_exists')}</div>
      {units.map(u => <div key={u.id}><Link to={`/units/${u.id}`}>{unitLine(u)}</Link>{u.vin && <span className="mono sub"> · {u.vin}</span>}</div>)}
      <ApplyBar a={a} />
    </Hint>
  );
}

// ---------- Carrier (USDOT / MC) ----------
type Carrier = { dot: string; mc: string | null; name: string; dba: string | null; address: string | null; phone: string | null;
  allowed_to_operate: boolean | null; power_units: number | null; drivers: number | null };

/** USDOT number or "MC 123456" → carrier name, address, phone; fills the customer fields once confirmed */
export function CarrierHint({ q, values, patch, field, nameField = 'company_name' }: { q: unknown; values: Values; patch: Patch; field: string; nameField?: string }) {
  const { t } = useTranslation();
  const raw = useDebounced(String(q ?? '').trim(), 450);
  const norm = raw.toUpperCase().replace(/[\s.#-]/g, '');
  const isMc = /^(MC|MX|FF)\d{3,8}$/.test(norm);
  const ok = isMc || /^(US)?(DOT)?\d{5,8}$/.test(norm);
  const status = useLookupStatus();
  const r = useQuery({
    queryKey: ['lookup-carrier', norm], enabled: ok && (!isMc || !!status.data?.carrier_mc), staleTime: Infinity, retry: false,
    queryFn: () => api.get<{ carrier: Carrier | null }>(`/lookup/carrier?q=${encodeURIComponent(norm)}`).then(x => x.carrier)
  });
  const c = r.data;
  const suggest = useMemo<Values>(() => c ? {
    [nameField]: c.dba ?? c.name, address: c.address, phone: c.phone, ...(isMc ? { [field]: c.dot } : {})
  } : {}, [c, nameField, field, isMc]);
  const a = useApply(norm, suggest, values, patch);

  if (!ok) return null;
  if (isMc && status.data && !status.data.carrier_mc) return <Hint>{t('lookup.mc_off')}</Hint>;
  if (r.isLoading) return <Hint>{t('lookup.searching')}</Hint>;
  if (r.isError) return null;
  if (c === null) return <Hint tone="warn">{t('lookup.carrier_none')}</Hint>;
  if (!c) return null;
  const facts = [`USDOT ${c.dot}`, c.mc && `MC ${c.mc}`, c.power_units != null && t('lookup.power_units', { n: c.power_units }), c.drivers != null && t('lookup.drivers', { n: c.drivers })].filter(Boolean);
  return (
    <Hint tone={c.allowed_to_operate === false ? 'warn' : 'ok'}>
      <div className="hint-title">{c.name}{c.dba && c.dba !== c.name ? ` (${c.dba})` : ''}</div>
      <div className="hint-specs">{[c.address, c.phone].filter(Boolean).join(' · ')}</div>
      <div className="hint-specs">{facts.join(' · ')}</div>
      {c.allowed_to_operate === false && <div className="hint-note">{t('lookup.not_allowed')}</div>}
      <ApplyBar a={a} />
    </Hint>
  );
}

/** The hint that goes under a lookup field in the generic Form */
export function LookupHint({ kind, name, values, patch, selfId }: { kind: 'vin' | 'plate' | 'carrier'; name: string; values: Values; patch: Patch; selfId?: number }) {
  const stablePatch = useMemo(() => patch, [patch]);
  if (kind === 'vin') return <VinHint vin={values[name]} values={values} patch={stablePatch} selfId={selfId} />;
  if (kind === 'plate') return <PlateHint plate={values[name]} values={values} patch={stablePatch} selfId={selfId} />;
  return <CarrierHint q={values[name]} values={values} patch={stablePatch} field={name} />;
}

// ---------- Factory data card on the unit page ----------
const VIN_ROWS: [string, string][] = [
  ['year', 'unit.year'], ['make', 'unit.make'], ['model', 'unit.model'], ['series', 'vin.series'], ['body_class', 'vin.body'],
  ['cab_type', 'vin.cab'], ['gvwr', 'vin.gvwr'], ['engine', 'vin.engine'], ['displacement_l', 'vin.displacement'], ['engine_hp', 'vin.hp'],
  ['fuel', 'vin.fuel'], ['transmission', 'vin.transmission'], ['drive_type', 'vin.drive'], ['brakes', 'vin.brakes'], ['axles', 'unit.axles'],
  ['wheelbase_in', 'vin.wheelbase'], ['trailer_body', 'vin.trailer_body'], ['trailer_length_ft', 'vin.trailer_length'], ['plant', 'vin.plant']
];

/** Everything the VIN says about the unit, from the NHTSA decoder */
export function VinDetails({ vin }: { vin: string | null | undefined }) {
  const { t } = useTranslation();
  const v = String(vin ?? '').toUpperCase();
  const r = useQuery({ queryKey: ['lookup-vin', v], enabled: VIN_OK.test(v), staleTime: Infinity, retry: false, queryFn: () => api.get<VinResult>(`/lookup/vin/${v}`) });
  const d = r.data?.vehicle;
  if (!d) return null;
  const vals: Record<string, string | undefined> = {
    ...d,
    make: d.make && d.make.charAt(0) + d.make.slice(1).toLowerCase(),
    engine: [d.engine_manufacturer, d.engine_model, d.engine_config && d.engine_cylinders && `${d.engine_config} ${d.engine_cylinders}`, d.turbo === 'Yes' ? 'Turbo' : undefined].filter(Boolean).join(' ') || undefined,
    displacement_l: d.displacement_l && `${Number(d.displacement_l).toFixed(1)} L`,
    engine_hp: d.engine_hp && String(Math.round(Number(d.engine_hp))),
    wheelbase_in: d.wheelbase_in && `${d.wheelbase_in} in`,
    trailer_length_ft: d.trailer_length_ft && `${d.trailer_length_ft} ft`,
    plant: [d.plant_city, d.plant_state, d.plant_country].filter(Boolean).map(s => s!.charAt(0) + s!.slice(1).toLowerCase()).join(', ') || undefined
  };
  const rows = VIN_ROWS.filter(([k]) => vals[k]);
  if (!rows.length) return null;
  return (
    <details className="card vin-card">
      <summary><b>{t('vin.title')}</b> <span className="sub">{[vals.year, vals.make, vals.model, vals.engine].filter(Boolean).join(' · ')}</span></summary>
      <dl className="kv">
        {rows.map(([k, label]) => <div key={k}><dt>{t(label)}</dt><dd>{vals[k]}</dd></div>)}
      </dl>
      <div className="sub" style={{ marginTop: 8 }}>{t('vin.source')}</div>
    </details>
  );
}
